import clsx from "clsx";
import React from "react";
import { Card, CardProps, Typography, Box, Pagination } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { ClientFormCardItem } from "./ClientFormCardItem";
import { useQuery } from "@tanstack/react-query";
import ClientFormsClientService from "../../services/ClientFormsClientService";
import { ClientFormCardItemSkeleton } from "./ClientFormCardItemSkeleton";
import { CardPagination } from "../pagination/CardPagination";
import { ReactComponent as FormsIcon } from "../../icons/FormsProfile.svg";
import { JourneyAnchors } from "../../constants";
import ClientDetailsCardHeader from "../coach-clients/ClientDetailsCardHeader";
import { ProfileIconBlock } from "../coach-clients/ProfileIconBlock";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },

  noText: {
    fontSize: 16,
    color: theme.palette.text.secondary,
    margin: theme.spacing(4, 0),
    textAlign: "center",
  },
}));

export interface ClientFormsCardProps extends CardProps {
  clientId?: number;
}

export const CLIENT_FORMS_CARD_QUERY_KEY = "client-forms-profile-card-query";
const PROFILE_FORMS_CARD_PAGE_SIZE = 4;

export function ClientFormsCard(props: ClientFormsCardProps) {
  const { className, clientId, ...other } = props;
  const s = useStyles();
  const [currentPage, setCurrentPage] = React.useState(1);
  const [isOpenContent, setIsOpenContent] = React.useState(true);
  const { data: clientFormsData, isLoading: clientFormsQueryInFlight } =
    useQuery({
      queryKey: [CLIENT_FORMS_CARD_QUERY_KEY, { clientId, currentPage }],
      queryFn: () =>
        ClientFormsClientService.getFormsList(
          currentPage,
          PROFILE_FORMS_CARD_PAGE_SIZE,
          clientId,
        ),
    });

  const clientForms = clientFormsData?.items ?? [];

  return (
    <Card
      className={clsx(s.root, className, JourneyAnchors.CLIENT_FORMS)}
      elevation={0}
      variant="outlined"
      {...other}
    >
      <ClientDetailsCardHeader
        icon={<ProfileIconBlock icon={<FormsIcon />} bgVariant="accent" />}
        title="Forms"
        isOpenContent={isOpenContent}
        setIsOpenContent={setIsOpenContent}
      />

      {isOpenContent && (
        <>
          {clientForms.length ? (
            clientForms.map((clientForm) => (
              <ClientFormCardItem
                key={clientForm.formId}
                clientForm={clientForm}
              />
            ))
          ) : clientFormsQueryInFlight ? (
            Array.from({ length: 3 }).map((_, i) => (
              <ClientFormCardItemSkeleton key={i} />
            ))
          ) : (
            <Typography className={s.noText}>No forms to show.</Typography>
          )}

          {clientFormsData?.totalPages > 1 && (
            <CardPagination
              page={currentPage}
              count={clientFormsData?.totalPages}
              onChange={(_, page) => setCurrentPage(page)}
            />
          )}
        </>
      )}
    </Card>
  );
}
